import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Building2, User, Hash, Briefcase } from "lucide-react";

const empty = { name: "", legal_rep: "", tax_number: "", sector: "", notes: "" };

export default function Companies() {
  const { t } = useI18n();
  const { isAdmin } = useAuth();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const { data: companies = [], isLoading } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });

  const saveMut = useMutation({
    mutationFn: async (payload) => editing ? (await api.put(`/companies/${editing}`, payload)).data : (await api.post("/companies", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["companies"] }); qc.invalidateQueries({ queryKey: ["dashboard"] }); setOpen(false); toast.success(t("save") + " ✓"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });
  const delMut = useMutation({
    mutationFn: async (id) => (await api.delete(`/companies/${id}`)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["companies"] }); toast.success("Deleted"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)),
  });

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (c) => { setEditing(c.id); setForm({ name: c.name, legal_rep: c.legal_rep || "", tax_number: c.tax_number || "", sector: c.sector || "", notes: c.notes || "" }); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name) { toast.error(t("required")); return; }
    setSaving(true);
    await saveMut.mutateAsync(form).catch(() => {});
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight sm:text-4xl">{t("companies")}</h1>
          <p className="mt-1 text-muted-foreground">{t("companiesSub")}</p>
        </div>
        {isAdmin && <Button onClick={openNew} data-testid="add-company-button" className="bg-renco-navy text-white hover:bg-renco-slate"><Plus className="mr-2 h-4 w-4" /> {t("addCompany")}</Button>}
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-renco-gold border-t-transparent" /></div>
      ) : companies.length === 0 ? (
        <Card className="p-12 text-center text-muted-foreground" data-testid="no-companies">{t("noCompanies")}</Card>
      ) : (
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {companies.map((c) => (
            <Card key={c.id} data-testid={`company-card-${c.id}`} className="animate-fade-up p-5 transition-transform hover:-translate-y-0.5 hover:shadow-md">
              <div className="flex items-start justify-between">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-renco-navy/10 text-renco-navy"><Building2 className="h-5 w-5" /></div>
                {isAdmin && (
                  <div className="flex gap-1">
                    <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`edit-company-${c.id}`} onClick={() => openEdit(c)}><Pencil className="h-4 w-4" /></Button>
                    <Button size="icon" variant="ghost" className="h-8 w-8 text-destructive hover:text-destructive" data-testid={`delete-company-${c.id}`} onClick={() => { if (window.confirm(t("confirmDeleteCompany"))) delMut.mutate(c.id); }}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                )}
              </div>
              <h3 className="mt-3 font-display text-lg font-semibold">{c.name}</h3>
              <div className="mt-3 space-y-1.5 text-sm text-muted-foreground">
                {c.legal_rep && <div className="flex items-center gap-2"><User className="h-3.5 w-3.5" /> {c.legal_rep}</div>}
                {c.tax_number && <div className="flex items-center gap-2"><Hash className="h-3.5 w-3.5" /> {c.tax_number}</div>}
                {c.sector && <div className="flex items-center gap-2"><Briefcase className="h-3.5 w-3.5" /> {c.sector}</div>}
              </div>
              <div className="mt-4 inline-flex rounded-full bg-renco-gold/15 px-3 py-1 text-xs font-semibold text-renco-gold">{c.document_count} {t("docs")}</div>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md" data-testid="company-modal">
          <DialogHeader><DialogTitle className="font-display">{editing ? t("editCompany") : t("addCompany")}</DialogTitle><DialogDescription>{t("companiesSub")}</DialogDescription></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="space-y-1.5"><Label>{t("companyName")}</Label><Input data-testid="company-name-input" value={form.name} onChange={(e) => setForm((s) => ({ ...s, name: e.target.value }))} required /></div>
            <div className="space-y-1.5"><Label>{t("legalRep")}</Label><Input data-testid="company-rep-input" value={form.legal_rep} onChange={(e) => setForm((s) => ({ ...s, legal_rep: e.target.value }))} /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>{t("taxNumber")}</Label><Input data-testid="company-tax-input" value={form.tax_number} onChange={(e) => setForm((s) => ({ ...s, tax_number: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>{t("sector")}</Label><Input data-testid="company-sector-input" value={form.sector} onChange={(e) => setForm((s) => ({ ...s, sector: e.target.value }))} /></div>
            </div>
            <div className="space-y-1.5"><Label>{t("notes")}</Label><Textarea data-testid="company-notes-input" rows={2} value={form.notes} onChange={(e) => setForm((s) => ({ ...s, notes: e.target.value }))} /></div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="company-cancel-button">{t("cancel")}</Button>
              <Button type="submit" disabled={saving} data-testid="company-save-button" className="bg-renco-navy text-white hover:bg-renco-slate">{saving ? t("saving") : t("save")}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
